import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, Users } from "lucide-react";
import { PLANS, money } from "./planConfig";
import CurrentPlanBanner from "./CurrentPlanBanner";

function AnnualHint({ planKey, monthlyEq }) {
  return (
    <div className="text-xs text-primary mt-1" data-testid={`plan-${planKey}-annual-hint`}>
      ~{money(monthlyEq)}/mo · 2 months free
    </div>
  );
}

function PlanHeader({ planKey, plan }) {
  const isFamily = planKey === "family";
  const highlight = plan.highlight;
  return (
    <div className={`text-sm font-medium uppercase tracking-widest flex items-center gap-2 ${highlight ? "text-primary" : "text-stone-500"}`}>
      {isFamily && <Users className="w-3.5 h-3.5" />}
      {plan.name}
    </div>
  );
}

export default function PlanCard({
  planKey, isAnnual, isPaid, tier, plan, busy, managing,
  trialEnd, periodEnd, onCheckout, onPreview, onPortal,
}) {
  const p = PLANS[planKey];
  const price = isAnnual ? p.annualPrice : p.monthlyPrice;
  const lookupKey = isAnnual ? p.annualLookup : p.monthlyLookup;
  const monthlyEq = isAnnual ? (p.annualPrice / 12).toFixed(2) : null;
  const isCurrent = isPaid && tier === planKey && plan?.interval === (isAnnual ? "year" : "month");
  const ctaLabel = isPaid ? `Switch to ${p.name}` : "Start 7-day free trial";
  const ctaClasses = p.highlight
    ? "bg-primary text-primary-foreground hover:bg-primary/90"
    : "bg-stone-900 text-white hover:bg-stone-800";

  return (
    <Card
      className={`p-8 rounded-2xl relative ${p.highlight ? "border-2 border-primary/40" : "border-border"}`}
      data-testid={`plan-${planKey}`}
    >
      {p.highlight && (
        <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Most popular
        </div>
      )}
      <PlanHeader planKey={planKey} plan={p} />
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-display text-5xl">{money(price)}</span>
        <span className="text-stone-500">/{isAnnual ? "year" : "month"}</span>
      </div>
      {isAnnual && <AnnualHint planKey={planKey} monthlyEq={monthlyEq} />}
      <p className="text-sm text-stone-500 mt-2">{p.tagline}</p>

      <ul className="mt-6 space-y-2 text-sm">
        {p.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <CurrentPlanBanner
          plan={plan} trialEnd={trialEnd} periodEnd={periodEnd}
          onPortal={onPortal} managing={managing} planKey={planKey}
        />
      ) : (
        <Button
          onClick={() => (isPaid ? onPreview(lookupKey) : onCheckout(lookupKey))}
          disabled={busy === lookupKey}
          className={`w-full mt-6 rounded-full ${ctaClasses} hover:-translate-y-0.5 transition-transform`}
          data-testid={`start-checkout-btn-${planKey}`}
        >
          {busy === lookupKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {ctaLabel}
        </Button>
      )}
    </Card>
  );
}
